/**
 * AudioManager.ts
 * Howler.js wrapper for music and SFX.
 * Handles Song Drop playback and unit buff cues (§3.3).
 */

import { Howl } from 'howler';

const MAX_WARMED_SNIPPETS = 3;
const MUSIC_ENABLED_KEY = 'fb_music_enabled';

export class AudioManager {
  private readonly warmedSnippets = new Map<string, Howl>();
  private readonly warmedOrder: string[] = [];

  preload(): Promise<void> {
    // Keep initial load light: snippets and songs are lazy-loaded on demand.
    return Promise.resolve();
  }

  playSongDrop(trackId: string): void {
    // TODO: play a real band track for a Song Drop and signal the buff window.
  }

  warmSnippet(url?: string): void {
    if (!this.musicEnabled) return;
    if (!url || this.warmedSnippets.has(url)) return;

    let sound: Howl;
    sound = new Howl({
      src: [url],
      html5: true,
      preload: true,
      volume: 0.8,
      onloaderror: () => {
        sound.unload();
        this.warmedSnippets.delete(url);
        this.removeWarmOrder(url);
      },
    });

    this.warmedSnippets.set(url, sound);
    this.warmedOrder.push(url);
    sound.load();
    this.pruneWarmCache();
  }

  playSnippet(url?: string): void {
    if (!this.musicEnabled) return;
    if (!url) return;

    const warmed = this.warmedSnippets.get(url);
    if (warmed) {
      try {
        if (warmed.state() === 'unloaded') warmed.load();
        warmed.stop();
        warmed.play();
      } catch {
        warmed.unload();
        this.warmedSnippets.delete(url);
        this.removeWarmOrder(url);
      }
      return;
    }

    let sound: Howl;
    sound = new Howl({
      src: [url],
      html5: true,
      preload: true,
      volume: 0.8,
      onend: () => sound.unload(),
      onloaderror: () => sound.unload(),
      onplayerror: () => sound.unload(),
    });

    try {
      sound.play();
    } catch {
      sound.unload();
    }
  }

  playSfx(id: string): void {
    // TODO: play a one-shot sound effect.
  }

  stopAll(): void {
    for (const sound of this.warmedSnippets.values()) {
      sound.unload();
    }
    this.warmedSnippets.clear();
    this.warmedOrder.length = 0;
  }

  private pruneWarmCache(): void {
    while (this.warmedOrder.length > MAX_WARMED_SNIPPETS) {
      const oldest = this.warmedOrder.shift();
      if (!oldest) continue;
      const sound = this.warmedSnippets.get(oldest);
      if (sound) sound.unload();
      this.warmedSnippets.delete(oldest);
    }
  }

  private removeWarmOrder(url: string): void {
    const index = this.warmedOrder.indexOf(url);
    if (index >= 0) this.warmedOrder.splice(index, 1);
  }

  private get musicEnabled(): boolean {
    return localStorage.getItem(MUSIC_ENABLED_KEY) !== 'false';
  }
}
